import { useEffect, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { GenieAvatar } from '@/components/GenieAvatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSubscription } from '@/contexts/billing/subscription';
import { useVerifySubscription } from '@/hooks/billing/useVerifySubscription';
import { supabase } from '@/integrations/supabase/client';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user } = useAuth();
  const { invalidateAndRefetch } = useSubscription();
  const { verifySubscription, isVerifying: isVerifyingMutation } = useVerifySubscription();
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [hasVerified, setHasVerified] = useState(false);

  const [newUserCreated, setNewUserCreated] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  useEffect(() => {
    if (hasVerified) return;

    if (!sessionId) {
      console.error("No session_id provided - cannot verify payment");
      setHasVerified(true);
      setVerificationSuccess(false);
      toast.error("Invalid Payment Link", {
        description: "This payment verification link is invalid or expired. Please contact support.",
      });
      return;
    }

    (async () => {
      try {
        console.log("Verifying subscription for user:", user?.email, "Session ID:", sessionId);

        const data = await verifySubscription({
          sessionId,
          userEmail: user?.email,
          userId: user?.id,
        });

        if (data.newUserCreated && !user) {
          setNewUserCreated(true);
          setUserEmail(data.userEmail!);

          // The account has a random password nobody knows. Sign in with the
          // single-use token from the server, then the buyer sets a password.
          const { error: signInError } = data.tokenHash
            ? await supabase.auth.verifyOtp({ token_hash: data.tokenHash, type: "magiclink" })
            : { error: new Error("No sign-in token returned") };

          if (signInError) {
            console.error("Auto sign-in error:", signInError);
            toast.error("Sign-in Error", {
              description: "Unable to sign in automatically. Please try signing in manually.",
            });
          }
        }

        if (data.success) {
          setVerificationSuccess(true);
          invalidateAndRefetch();

          toast.success("Subscription Verified", {
            description: "Your Help Genie subscription has been successfully activated.",
          });
        } else {
          toast.error("Verification Failed", {
            description: "Unable to verify your subscription. Please contact support.",
          });
        }
      } catch (error) {
        console.error("Subscription verification error:", error);
        toast.error("Verification Error", {
          description: "An error occurred while verifying your subscription.",
        });
      } finally {
        setHasVerified(true);
      }
    })();
  }, [sessionId, user, hasVerified, verifySubscription, invalidateAndRefetch]);

  const handlePasswordUpdate = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Invalid Password", {
        description: "Password must be at least 6 characters long.",
      });
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast.success("Password Updated", {
        description: "Your password has been successfully updated.",
      });

      setNewUserCreated(false);
    } catch (error) {
      console.error("Password update error:", error);
      toast.error("Password Update Failed", {
        description: "Unable to update your password. Please try again.",
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleContinue = () => {
    navigate("/genie-hub/my-genies");
  };

  const handleCheckSubscription = () => {
    navigate("/genie-hub/account-settings");
  };

  // --- Verifying state ---
  if (isVerifyingMutation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center space-y-8 max-w-sm mx-auto">
          <div className="w-32 h-32 mx-auto">
            <GenieAvatar speaking={true} />
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-gray-900">Verifying Your Subscription</h2>
            <p className="text-sm text-gray-500">Setting up your Help Genie experience...</p>
          </div>

          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
            <span className="text-sm text-gray-500">This will only take a moment</span>
          </div>
        </div>
      </div>
    );
  }

  // --- Error state (no session ID) ---
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-8">
          <div className="w-28 h-28 mx-auto opacity-40">
            <GenieAvatar />
          </div>

          <div className="space-y-3">
            <h1 className="text-2xl font-bold text-gray-900">Unable to Verify Payment</h1>
            <p className="text-sm text-gray-500">
              This verification link is invalid or has expired. If you believe this is an error, please contact support.
            </p>
          </div>

          <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[10px] bg-white p-6 space-y-4">
              <Button
                onClick={() => navigate("/pricing")}
                className="w-full bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 transition-all duration-200"
              >
                View Pricing Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="w-full"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Success / Processing state ---
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6">
      <div className="max-w-lg w-full text-center space-y-8 py-12">
        {/* Avatar */}
        <div className="w-36 h-36 mx-auto">
          <GenieAvatar speaking={verificationSuccess} />
        </div>

        {/* Heading */}
        <div className="space-y-3">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            {verificationSuccess ? (
              <>
                Welcome to{" "}
                <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                  Help Genie
                </span>
              </>
            ) : (
              "Payment Received"
            )}
          </h1>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            {verificationSuccess
              ? "Your subscription is active. You're all set to create your first voice assistant."
              : "We received your payment and are setting up your account."}
          </p>
        </div>

        {/* Password setup for new users */}
        {newUserCreated && (
          <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[10px] bg-white p-6 text-left space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Set Your Password</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Choose a password for <CopyableEmail email={userEmail} className="font-bold text-genie-primary" />
                </p>
              </div>

              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-gray-100 rounded"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>

              <Button
                onClick={handlePasswordUpdate}
                className="w-full bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 transition-all duration-200 disabled:opacity-50"
                disabled={!newPassword || newPassword.length < 6 || isUpdatingPassword}
              >
                {isUpdatingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Setting Up...
                  </>
                ) : (
                  <>
                    Set Password & Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Processing banner */}
        {!verificationSuccess && (
          <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
              <p className="text-sm font-medium text-gray-700">Activating your subscription</p>
            </div>
            <p className="text-xs text-gray-500">
              This usually takes just a few moments. You'll be able to get started once processing is complete.
            </p>
          </div>
        )}

        {/* Action buttons */}
        {verificationSuccess && !newUserCreated && (
          <div className="space-y-3">
            <Button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-[1.02] transition-all duration-200"
            >
              Start Creating Genies
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              onClick={handleCheckSubscription}
              variant="outline"
              className="w-full"
            >
              Account Settings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
